import { defaultPromptLevelColors, targetDataTypes, workflowTypes } from 'boot/globals';
import { chain, concat, get, has, isEmpty, keyBy, merge, reject, sortBy, sumBy } from 'lodash';
import { mapState } from 'pinia';
import { colors } from 'quasar';
import DataDB from 'src/fb/data-db';
import ProgramsDB from 'src/fb/programs-db';
import ProgramTargetsDB from 'src/fb/programTargets-db';
import SessionsDB from 'src/fb/sessions-db';
import { promptLevels } from 'src/store/promptLevels';
import { countTargetsPerStatus, toTarget } from 'src/contexts/treatmentPlanning';
import { workflows } from 'src/store/workflows';
import {
  getTargetDataType,
  getTargetMetric,
  getTargetType,
  getTextColor,
} from 'src/utils/TargetUtils';

export default {

  computed: {
    ...mapState(promptLevels, ['promptLevelTemplates']),
    ...mapState(workflows, ['workflows']),
  },

  methods: {

    getDataType(programType) {
      switch (programType) {
        case 'frequency':
          return 'Frequency1.0';
        case 'interval':
        case 'esdm':
          return 'Interval1.0';
        case 'duration':
          return 'Duration1.0';
        case 'taskAnalysis':
        case 'sbt':
          return 'Task1.0';
        default:
          return 'Trial2.0';
      }
    },

    getAvailableTargetDataTypes(programType, hasParentTarget) {
      return chain(targetDataTypes)
        .filter((targetDataType) => targetDataType.allowedProgramTypes.includes(programType))
        .reject((targetDataType) => {
          const isRandomRotationTarget = targetDataType.value === 'randomRotation';
          const isRandomRotationParentTarget = get(this.parentTarget, 'dataType', '') === 'randomRotation';

          return (hasParentTarget && !targetDataType.allowAsSubTarget)
            || (isRandomRotationTarget && isRandomRotationParentTarget);
        })
        .value();
    },

    getPromptLevelColor(promptLevel, fallbackScore = -1) {
      if (has(promptLevel, 'color')) {
        return promptLevel.color;
      }
      if (get(promptLevel, 'score', fallbackScore) === 100) {
        return defaultPromptLevelColors.successful;
      }
      if (get(promptLevel, 'score', fallbackScore) === -1) {
        return defaultPromptLevelColors.unknown;
      }
      return defaultPromptLevelColors.default;
    }, // getPromptLevelColor()

    getPromptLevelTextColor(promptLevel, fallbackScore = -1) {
      const bgColor = this.getPromptLevelColor(promptLevel, fallbackScore);
      return getTextColor(bgColor);
    }, // getPromptLevelTextColor()

    getPromptLevelStyle(promptLevel, fallbackScore = -1) {
      const bgColor = this.getPromptLevelColor(promptLevel, fallbackScore);
      const textColor = getTextColor(bgColor);
      const borderColor = colors.lighten(bgColor, -20);
      return `color:${textColor};background-color:${bgColor};border-color:${borderColor}`;
    }, // getPromptLevelStyle()

    /**
     * Number of targets per status, stored on the program so the program list does not have to read
     * every target — Firestore cannot count without reading.
     *
     * Delegates to `src/contexts/treatmentPlanning`, which owns the rule. This method stays as the
     * mixin's seam so the components using it do not each grow an import; the counting itself has
     * one implementation now rather than six.
     *
     * `editStatus` is filtered HERE rather than in the context. It is the target editor's
     * unsaved-changes marker — a fact about a form, not about a target — so it stops at the boundary
     * instead of teaching the domain what a staged deletion is.
     */
    getTargetsPerStatus(programTargets) {
      const live = (programTargets ?? []).filter((programTarget) => programTarget.editStatus !== 'delete');

      return countTargetsPerStatus(live.map((programTarget) => toTarget(programTarget)));
    }, // getTargetsPerStatus()

    async saveTargetChanges(programTargets, programId) {
      const programTargetsDb = new ProgramTargetsDB(programId);

      const saveActions = programTargets.filter((programTarget) => programTarget.editStatus).map((programTarget) => {
        if (programTarget.editStatus === 'delete') {
          return programTargetsDb.delete(programTarget.id);
        }
        const programTargetObj = { ...programTarget };
        delete programTargetObj.editStatus;
        if (programTarget.editStatus === 'create' && programTarget.label) {
          return programTargetsDb.create(programTargetObj);
        }
        return programTargetsDb.update(programTargetObj);
      });

      await Promise.all(saveActions);
    }, // saveTargetChanges

    async updateTargetsPerStatus(program) {
      const programTargetsDb = new ProgramTargetsDB(program.id);
      const programTargets = await programTargetsDb.readAll();

      if (isEmpty(programTargets)) {
        return;
      }

      const targetsPerStatus = this.getTargetsPerStatus(programTargets);

      const programsDb = new ProgramsDB();
      await programsDb.set({ targetsPerStatus }, program.id);
    }, // updateTargetsPerStatus()

    async updateTargetsPerStatusWithTransaction(program, programTargets, transactionMethods) {
      if (isEmpty(programTargets)) {
        return;
      }

      const targetsPerStatus = this.getTargetsPerStatus(programTargets);

      const programsDb = new ProgramsDB();
      await transactionMethods.set(programsDb, { data: targetsPerStatus, id: program.id });
    }, // updateTargetsPerStatus()

    async loadProgramData(program) {
      if (!program.id) {
        return [];
      }

      const dataDb = new DataDB();
      // Load data from all sessions using target parent program. We need
      // to load all matching programs because Firebase does not allow us
      // to query inside data array of the Data Collection
      const data = await dataDb.readAll(
        [
          ['client', '==', program.client],
          ['program', '==', program.id]],
        //        'runTimestamp',
      );

      // Do not return soft-deleted data
      return reject(data, { isDeleted: true });
    }, // loadProgramData()

    /**
     * Load trial data for a target and organize it chronological by session
     */
    async filterTargetData(
      loadedData,
      target,
      { loadPromptLevels = false, skipSessions = null, loadSessions = null, addOverallIndexes = false, program = null },
    ) {
      if (!target.id) {
        return [];
      }

      // Filter out data recorded with other targets than specified
      const filteredData = chain(loadedData)
        .map((d) => {
          return {
            ...d,
            data: chain(d.data)
              .reject((dataItem) => !has(dataItem, 'trial')
                && !(dataItem.phantomTrial && getTargetType(target, program) === 'duration'))
              .filter({ target: target.id })
              .map((dataItem) => {
                return loadPromptLevels ? {
                  ...dataItem,
                  promptLevelDetails: chain(this.promptLevelTemplates)
                    .find({ id: dataItem.promptLevelTemplate || d.promptLevelTemplate })
                    .get('promptLevels')
                    .find({ id: dataItem.promptLevel })
                    .value(),
                }
                  : dataItem;
              })
              .value(),
          };
        })
        // Remove sessions where target was not run
        .reject((d) => (isEmpty(get(d, 'data'))))
        .groupBy('session')
        .map((sessionPrograms, sessionId) => ({
          data: chain(sessionPrograms)
            .reduce((pV, cV) => concat(pV, cV.data), [])
            .sortBy('time')
            .map((dataItem, trialIndex) => ({ ...dataItem, trial: trialIndex + 1 }))
            .sortBy('trial')
            .value(),
          // @todo Due to a small bug we sometimes save timestamps in "data"
          // as objects and not Firestore Timestamps.
          runTimestamp: new Date(chain(sessionPrograms)
            .head()
            .get(['createTimestamp', 'seconds'])
            .value() * 1000),
          runByName: null,
          sessionId,
          sessionName: null,
          duration: sumBy(sessionPrograms, 'duration') || 0,
        }
        ))
        .sortBy('runTimestamp')
        .value();

      let sessionTrialData;

      if (loadSessions !== null) {
        const sessionLimit = loadSessions || 0;
        let trialSessions;
        if (skipSessions === null) {
          trialSessions = chain(filteredData)
            .slice(-sessionLimit)
            .map('sessionId')
            .uniq()
            .value();
        } else {
          trialSessions = chain(filteredData)
            .slice(-(skipSessions + loadSessions), -skipSessions)
            .map('sessionId')
            .uniq()
            .value();
        }
        const sessionsDb = new SessionsDB();
        let sessions = await sessionsDb.readMultiple(trialSessions);
        sessions = keyBy(sessions, 'id');
        sessionTrialData = chain(filteredData)
          .map((sessionInfo) => {
            if (!get(sessions, sessionInfo.sessionId)) {
              return null;
            }

            const session = get(sessions, sessionInfo.sessionId);

            return {
              ...sessionInfo,
              runByName: session.runByName || null,
              sessionName: session.sessionName || null,
            };
          })
          .compact()
          .value();
      } else {
        sessionTrialData = filteredData;
      }

      if (addOverallIndexes) {
        // Enumerate trials
        let targetTrial = 1;
        sessionTrialData.forEach((session, sessionIndex) => {
          session.data.forEach((trial, trialIndex) => {
            sessionTrialData[sessionIndex].data[trialIndex].targetTrial = targetTrial;
            targetTrial += 1;
          });
        });
      }

      return sessionTrialData;
    }, // filterTargetData()

    async loadTargetData(
      program,
      target,
      { loadPromptLevels = false, skipSessions = null, loadSessions = null, addOverallIndexes = false },
    ) {
      if (!program.id || !target.id) {
        return [];
      }

      const loadedData = await this.loadProgramData(program);

      return this.filterTargetData(
        loadedData,
        target,
        { loadPromptLevels, skipSessions, loadSessions, addOverallIndexes, program },
      );
    }, // loadTargetData()

    /**
     * Returns array of prompt levels based on trial data (in format returned by loadTargetData)
     * @param {*} trialData
     */
    getPromptLevels(trialData) {
      return chain(trialData)
        .flatMap((sessionTrialData) => chain(sessionTrialData.data)
          .map((data) => ({
            promptLevelId: data.promptLevel,
            promptLevel: data.promptLevelDetails,
          }))
          .value())
        .uniqBy('promptLevelId')
        .reject(['promptLevel', null])
        .sortBy('promptLevel.order')
        .keyBy('promptLevelId')
        .value();
    }, // getPromptLevels()

    /**
     * Rotate a random rotation target and return any updates to sessionProduct data object
     */
    rotateTarget(target) {
      if (this.disable || (!get(target, 'id')) || target.dataType !== 'randomRotation') {
        return;
      }

      let currentTarget = null;
      if (!chain(target).get('subTargets', []).isEmpty().value()) {
        const targetIdx = Math.floor(Math.random() * chain(target).get('subTargets', []).size().value());
        currentTarget = chain(target).get(['subTargets', targetIdx]).value();
      }

      return { [`rrTarget_${target.id}`]: get(currentTarget, 'id', null) };
    }, // rotateTarget

    getAdjustedWorkflow(workflowStatuses, program, target = null, parentTarget = null) {
      const workflowId = target?.workflow
        || parentTarget?.defaultWorkflow
        || program?.workflow
        || 'default';

      if (target !== null && workflowId !== 'default') {
        // Check if the active workflow type is compatible with the target measurement metric
        const workflow = this.workflows.find((w) => w.id === workflowId);
        if (workflow) {
          const workflowType = workflowTypes[workflow.type || 'accuracyIncrease'];
          const targetMetric = this.getTargetMetric(target, program);

          if (workflowType?.metric !== targetMetric && workflow.type !== 'manual') {
            // Active workflow is not compatible with the target measurement metric
            return get(workflowStatuses, 'default', {});
          }
        }
      }

      const workflowStatusInfo = get(workflowStatuses, workflowId, {});
      // When target has its own explicit workflow, inherited adjustments
      // (from program OR parent target) belong to a different workflow and must be ignored.
      const hasOwnWorkflow = !!target?.workflow;
      const programWorkflowAdjustments = hasOwnWorkflow
        ? {}
        : get(program, 'workflowAdjustments', {});
      const parentTargetWorkflowAdjustments = hasOwnWorkflow
        ? {}
        : get(parentTarget, 'defaultWorkflowAdjustments', {});
      const targetWorkflowAdjustments = get(target, 'workflowAdjustments', {});

      return merge(
        {},
        workflowStatusInfo,
        programWorkflowAdjustments,
        parentTargetWorkflowAdjustments,
        targetWorkflowAdjustments,
      );
    }, // getAdjustedWorkflow()

    getActiveWorkflow(programElement, field) {
      return chain(this.workflows)
        .find({ id: get(programElement, field, null) })
        .get('id')
        .defaultTo(null)
        .value();
    }, // getActiveWorkflow()

    getTargetWorkflowDetails(workflowStatuses, program, target, property, parentTarget, defaultValue = null) {
      return get(
        this.getAdjustedWorkflow(workflowStatuses, program, target, parentTarget),
        [target.status, property],
        defaultValue,
      );
    }, // getTargetWorkflowDetails()

    /**
     * @param {Object} workflowStatuses
     * @param {Object} program
     * @param {Array} programTargets
     * @param {Object} sessionProgramSelectedTargets
     * @param {Array} hiddenTargets
     * @return {Object}
     */
    prepareTargetsForDataCollection(
      workflowStatuses,
      program,
      programTargets,
      sessionProgramSelectedTargets,
      hiddenTargets,
    ) {
      const getSubTargets = (targets, parentTarget, depth = 0) => {
        if (depth > 4) {
          // Disallow very deeply nested targets
          return [];
        }
        return chain(targets)
          .filter({ parentTarget: get(parentTarget, 'id', null) })
          .filter(
            (target) => (
              (get(parentTarget, 'dataType') === 'taskAnalysis')
              && (['waiting', 'acquisition', 'mastered'].includes(
                this.getTargetWorkflowDetails(workflowStatuses, program, target, 'type', parentTarget),
              )
              )
            )
              || (get(
                sessionProgramSelectedTargets,
                this.getTargetWorkflowDetails(workflowStatuses, program, target, 'type', parentTarget),
                false,
              )
                && !hiddenTargets.includes(target.id)),
          )
          .map((target) => ({
            ...target,
            subTargets: getSubTargets(targets, target, depth + 1),
          }
          ))
          .value();
      };

      if (['taskAnalysis', 'sbt'].includes(program.type)) {
        // Do not reorder TA programs
        return getSubTargets(programTargets, null);
      }
      return sortBy(
        getSubTargets(programTargets, null),
        (target) => (
          (
            this.getTargetWorkflowDetails(workflowStatuses, program, target, 'order') * 1000)
          + target.order
        ),
      );
    }, // prepareTargetsForDataCollection()

    /**
     * Get labels of all targets (including subtargets) keyed by the id of the target.
     *
     * @param {Object[]} programTargets
     * @return {Record<string, string>}
     */
    getTargetsLabelsKeyedById(programTargets) {
      if (!Array.isArray(programTargets) || !programTargets?.length) {
        return {};
      }

      return programTargets.reduce((acc, target) => {
        if (Array.isArray(target.subTargets) && target.subTargets.length > 0) {
          return {
            ...acc,
            ...this.getTargetsLabelsKeyedById(target.subTargets),
          };
        }

        acc[target.id] = target.label || '';
        return acc;
      }, {});
    }, // getTargetsLabelsKeyedById()

    // @deprecated Use getTargetDataType() directly from TargetUtils instead. Preserved here for backward compatibility.
    getTargetDataType(target, program) {
      return getTargetDataType(target.dataType, program?.type);
    },

    // @deprecated Use getTargetMetric() directly from TargetUtils instead. Preserved here for backward compatibility.
    getTargetMetric(target, program) {
      return getTargetMetric(target.primaryMetric, target.dataType, program.primaryMetric, program.type);
    }, // getTargetMetric()

  },
};
